import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Phone, Mail, Upload, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '', gender: '', birthday: '' });
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [page, setPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const fileRef = useRef(null);
  const searchTimeout = useRef(null);

  const fetchClients = useCallback(async (pg = 1, q = '') => {
    try {
      const { data } = await axios.get(`${API}/clients?page=${pg}&limit=50&search=${encodeURIComponent(q)}`, { withCredentials: true });
      setClients(data.clients);
      setTotalClients(data.total);
      setTotalPages(data.pages);
      setPage(data.page);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchClients(1, ''); }, [fetchClients]);

  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchClients(1, val), 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/clients/${editId}`, form, { withCredentials: true });
        toast.success(t('clients.clientUpdated'));
      } else {
        await axios.post(`${API}/clients`, form, { withCredentials: true });
        toast.success(t('clients.clientAdded'));
      }
      setDialogOpen(false);
      resetForm();
      fetchClients(page, search);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('clients.failed'));
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm(t('clients.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/clients/${id}`, { withCredentials: true });
      toast.success(t('clients.clientDeleted'));
      fetchClients(page, search);
    } catch (err) { toast.error(t('clients.failedDelete')); }
  };

  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name, email: c.email || '', phone: c.phone || '', notes: c.notes || '', gender: c.gender || '', birthday: c.birthday || '' });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditId(null);
    setForm({ name: '', email: '', phone: '', notes: '', gender: '', birthday: '' });
  };

  // CSV Preview
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast.error('CSV file is empty or has no data rows');
        return;
      }
      const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const rows = [];
      for (let i = 1; i < Math.min(lines.length, 6); i++) {
        const values = lines[i].match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [];
        const row = {};
        header.forEach((h, idx) => { row[h] = (values[idx] || '').replace(/^"|"$/g, '').trim(); });
        rows.push(row);
      }
      setCsvPreview({ header, rows, totalRows: lines.length - 1 });
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const { data } = await axios.post(`${API}/clients/import-csv`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(data);
      const parts = [];
      if (data.imported > 0) parts.push(`${data.imported} imported`);
      if (data.merged > 0) parts.push(`${data.merged} merged`);
      if (data.skipped > 0) parts.push(`${data.skipped} unchanged`);
      if (parts.length > 0) {
        toast.success(parts.join(', '));
        fetchClients(1, search);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportResult(null);
    setCsvPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="fade-in space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>{t('clients.title')}</h1>
          <p className="text-sm text-[#5C6656] mt-1">{totalClients} {t('clients.totalClients').toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6656]" />
            <Input data-testid="client-search" placeholder={t('clients.search')} value={search} onChange={e => handleSearch(e.target.value)} className="pl-9 w-52 border-[#E5E7E1]" />
          </div>
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) resetImport(); }}>
            <DialogTrigger asChild>
              <Button data-testid="import-clients-btn" variant="outline" className="border-[#556B2F] text-[#556B2F] hover:bg-[#E9EDDF]">
                <Upload size={14} className="mr-1" /> {t('clients.importCSV')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg border-[#E5E7E1]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>{t('clients.importDialogTitle')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="p-4 bg-[#F9F8F6] rounded-xl border border-[#E5E7E1]">
                  <div className="flex items-start gap-3">
                    <FileSpreadsheet size={20} className="text-[#556B2F] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-[#1A2421]">{t('clients.importSubtitle')}</p>
                      <p className="text-xs text-[#5C6656] mt-1">{t('clients.importDesc')}</p>
                      <p className="text-xs text-[#5C6656] mt-1">{t('clients.importDedupeNote')}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Input
                    ref={fileRef}
                    data-testid="csv-file-input"
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileSelect}
                    className="border-[#E5E7E1]"
                  />
                </div>

                {csvPreview && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-[#E9EDDF] text-[#556B2F]">{t('clients.rowsDetected', { count: csvPreview.totalRows })}</Badge>
                      <span className="text-xs text-[#5C6656]">{t('clients.showingFirstRows')}</span>
                    </div>
                    <div className="overflow-x-auto border border-[#E5E7E1] rounded-lg">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="bg-[#F9F8F6]">
                            {csvPreview.header.slice(0, 6).map(h => (
                              <th key={h} className="px-2 py-1.5 text-left font-medium text-[#5C6656] border-b border-[#E5E7E1]">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreview.rows.map((row, i) => (
                            <tr key={i} className="border-b border-[#E5E7E1]/50">
                              {csvPreview.header.slice(0, 6).map(h => (
                                <td key={h} className="px-2 py-1 text-[#1A2421] max-w-[120px] truncate">{row[h] || '-'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {importResult && (
                  <div className="p-4 rounded-xl border border-[#E5E7E1] space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-[#3E6641]" />
                      <span className="text-sm font-medium text-[#1A2421]">{t('clients.importComplete')}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="p-2 bg-[#E9EDDF] rounded-lg">
                        <p className="text-lg font-bold text-[#3E6641]">{importResult.imported}</p>
                        <p className="text-[10px] text-[#5C6656] uppercase font-bold">{t('clients.importNew')}</p>
                      </div>
                      <div className="p-2 bg-[#E9EDDF] rounded-lg">
                        <p className="text-lg font-bold text-[#556B2F]">{importResult.merged || 0}</p>
                        <p className="text-[10px] text-[#5C6656] uppercase font-bold">{t('clients.importMerged')}</p>
                      </div>
                      <div className="p-2 bg-[#F9F8F6] rounded-lg">
                        <p className="text-lg font-bold text-[#D4A373]">{importResult.skipped}</p>
                        <p className="text-[10px] text-[#5C6656] uppercase font-bold">{t('clients.importUnchanged')}</p>
                      </div>
                      <div className="p-2 bg-[#F9F8F6] rounded-lg">
                        <p className="text-lg font-bold text-[#E2725B]">{importResult.errors?.length || 0}</p>
                        <p className="text-[10px] text-[#5C6656] uppercase font-bold">{t('clients.importErrors')}</p>
                      </div>
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div className="text-xs text-[#E2725B] space-y-1 mt-2">
                        {importResult.errors.slice(0, 5).map((e, i) => (
                          <div key={i} className="flex items-center gap-1"><AlertCircle size={10} />{e}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {csvPreview && !importResult && (
                  <Button data-testid="run-import-btn" onClick={handleImport} disabled={importing} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">
                    {importing ? t('clients.importing') : t('clients.importCountClients', { count: csvPreview.totalRows })}
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-[#556B2F] hover:bg-[#4A5D23] text-white">
                <Plus size={16} className="mr-1" /> {t('clients.addClientBtn')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md border-[#E5E7E1]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>{editId ? t('clients.editClient') : t('clients.newClient')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label>{t('clients.nameRequired')}</Label>
                  <Input data-testid="client-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="border-[#E5E7E1]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('clients.email')}</Label>
                    <Input data-testid="client-email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="border-[#E5E7E1]" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('clients.phone')}</Label>
                    <Input data-testid="client-phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="border-[#E5E7E1]" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('clients.gender')}</Label>
                    <Input data-testid="client-gender" value={form.gender} onChange={e => setForm(p => ({ ...p, gender: e.target.value }))} className="border-[#E5E7E1]" placeholder={t('clients.optionalPlaceholder')} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('clients.birthday')}</Label>
                    <Input data-testid="client-birthday" type="date" value={form.birthday} onChange={e => setForm(p => ({ ...p, birthday: e.target.value }))} className="border-[#E5E7E1]" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('clients.notesField')}</Label>
                  <Textarea data-testid="client-notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="border-[#E5E7E1]" />
                </div>
                <Button data-testid="submit-client" type="submit" className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">
                  {editId ? t('clients.updateClient') : t('clients.addClientBtn')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-[#E5E7E1] shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E7E1]">
                <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">{t('clients.name')}</TableHead>
                <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">{t('clients.contact')}</TableHead>
                <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">{t('clients.details')}</TableHead>
                <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656] text-right">{t('clients.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#5C6656]">{t('clients.noClientsFound')}</TableCell></TableRow>
              ) : (
                clients.map(c => (
                  <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="border-[#E5E7E1] hover:bg-[#F9F8F6]">
                    <TableCell>
                      <button onClick={() => navigate(`/clients/${c.id}`)} className="font-medium text-[#556B2F] hover:underline text-left" data-testid={`client-name-${c.id}`}>{c.name}</button>
                      {c.gender && <span className="text-xs text-[#5C6656] block">{c.gender}</span>}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {c.email && <div className="flex items-center gap-1 text-sm text-[#5C6656]"><Mail size={12} />{c.email}</div>}
                        {c.phone && <div className="flex items-center gap-1 text-sm text-[#5C6656]"><Phone size={12} />{c.phone}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {c.birthday && <span className="text-xs text-[#5C6656]">{t('clients.bornLabel')}: {c.birthday}</span>}
                        {c.notes && <p className="text-xs text-[#5C6656] max-w-[200px] truncate">{c.notes}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="text-[#556B2F]"><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteClient(c.id)} className="text-[#E2725B]"><Trash2 size={14} /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-3 border-t border-[#E5E7E1]">
              <p className="text-xs text-[#5C6656]">{t('clients.pageOf', { page, total: totalPages, totalCount: totalClients })}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchClients(page - 1, search)} className="h-7 text-xs" data-testid="prev-page">{t('clients.prev')}</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => fetchClients(page + 1, search)} className="h-7 text-xs" data-testid="next-page">{t('clients.next')}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
